// Off-diff comments: comments on lines GitHub will not take a line review
// comment on (outside the PR's diff hunks, or in files the PR does not
// change). Their bodies carry a visible header naming the file and line, and
// end with a hidden anchor marker that changelist reads back to show the
// comment at its line:
//
//   **`path/to/file.rs` line 42** · [view](https://github.com/o/r/blob/<commit>/path/to/file.rs#L42)
//   > the quoted line
//
//   <user's text>
//
//   <!-- changelist:anchor {"v":1,"path":"…","line":42,"side":"RIGHT","commit":"<40-hex>"} -->

export type DiffSide = 'LEFT' | 'RIGHT';

/** The anchor a marker records. `start` is present only for ranges (start < line). */
export interface AnchorMarker {
  v: 1;
  path: string;
  line: number;
  start?: number;
  side: DiffSide;
  commit: string;
}

/** A body with a valid marker, split into its parts. */
export interface OffDiffBody {
  marker: AnchorMarker;
  /** The user's text, without header and marker. */
  text: string;
  /** Quoted lines from the header (unescaped), or null when the body has no recognizable header. */
  quote: string[] | null;
  /** Everything before the user's text (the header and the blank line after it), possibly empty. */
  prefix: string;
  /** Everything after the user's text (the blank line and the marker). */
  suffix: string;
}

/** Longest quoted line, in UTF-16 code units, before it is cut with "…". */
export const QUOTE_MAX = 200;
const MARKER_OPEN = '<!-- changelist:anchor ';
const MAX_LINE = 10_000_000;
const MAX_PATH = 4096;

// ---------------------------------------------------------------- building

/**
 * The marker comment. Its JSON never contains `--` or `>` (each `-` and `>`
 * is written as a JSON \u escape), so no value can end the HTML comment.
 */
export function encodeMarker(m: AnchorMarker): string {
  const ordered: Record<string, unknown> = { v: 1, path: m.path, line: m.line };
  if (m.start !== undefined && m.start < m.line) ordered.start = m.start;
  ordered.side = m.side;
  ordered.commit = m.commit;
  const json = JSON.stringify(ordered).replace(/-/g, '\\u002d').replace(/>/g, '\\u003e');
  return `${MARKER_OPEN}${json} -->`;
}

/** A source line as it appears in a header quote: trimmed and cut at QUOTE_MAX. */
export function quoteText(line: string): string {
  const t = line.trim();
  return t.length > QUOTE_MAX ? t.slice(0, QUOTE_MAX) + '…' : t;
}

/**
 * Backslash-escape what Markdown would interpret in a quoted line (emphasis,
 * code, links, HTML, @mentions, #references, emoji, and list or heading
 * markers at its start), so it renders literally and notifies nobody.
 */
export function escapeMarkdown(s: string): string {
  return s
    .replace(/[\\`*_[\]<>&~|!#@:]/g, (c) => '\\' + c)
    .replace(/^([+\-=])/, '\\$1')
    .replace(/^(\d+)([.)])/, '$1\\$2');
}

export function unescapeMarkdown(s: string): string {
  return s.replace(/\\([!-/:-@[-`{-~])/g, '$1');
}

/** A path as inline code, with a fence longer than any backtick run inside it. */
function inlineCode(s: string): string {
  const longest = Math.max(0, ...(s.match(/`+/g) ?? []).map((r) => r.length));
  const fence = '`'.repeat(longest + 1);
  const pad = longest || s.startsWith('`') || s.endsWith('`') ? ' ' : '';
  return fence + pad + s + pad + fence;
}

/** `path` URL-encoded per segment. */
export function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/** GitHub permalink to a file at a commit. */
export function fileUrl(webUrl: string, owner: string, repo: string, commit: string, path: string): string {
  const base = webUrl.replace(/\/+$/, '');
  return `${base}/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/blob/${commit}/${encodePath(path)}`;
}

/** GitHub permalink to a line (or range) of a file at a commit. */
export function blobUrl(webUrl: string, owner: string, repo: string, m: Pick<AnchorMarker, 'path' | 'line' | 'start' | 'commit'>): string {
  const frag = m.start !== undefined && m.start < m.line ? `#L${m.start}-L${m.line}` : `#L${m.line}`;
  return fileUrl(webUrl, owner, repo, m.commit, m.path) + frag;
}

export function lineLabel(m: Pick<AnchorMarker, 'line' | 'start'>): string {
  return m.start !== undefined && m.start < m.line ? `lines ${m.start}–${m.line}` : `line ${m.line}`;
}

export interface BuildInput {
  marker: AnchorMarker;
  /** Source text of the selected lines, first to last. */
  lines: string[];
  /** The user's comment. */
  text: string;
  webUrl: string;
  owner: string;
  repo: string;
}

/** The full comment body: header, quote, the user's text, marker. */
export function buildOffDiffBody(input: BuildInput): string {
  const m = normalizeMarker(input.marker);
  if (!m) throw new Error('invalid anchor');
  const header = `**${inlineCode(m.path)} ${lineLabel(m)}** · [view](${blobUrl(input.webUrl, input.owner, input.repo, m)})`;
  const last = input.lines[input.lines.length - 1] ?? '';
  const picked = m.start !== undefined ? [input.lines[0] ?? '', last] : [last];
  const quote = picked.map((l) => {
    const q = escapeMarkdown(quoteText(l));
    return q ? '> ' + q : '>';
  });
  const text = input.text.replace(/\r\n?/g, '\n').trim();
  return `${header}\n${quote.join('\n')}\n\n${text}\n\n${encodeMarker(m)}`;
}

// ---------------------------------------------------------------- parsing

function isLine(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= MAX_LINE;
}

/** A well-formed marker, or null. */
export function normalizeMarker(raw: unknown): AnchorMarker | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.v !== 1) return null;
  const { path, line, start, side, commit } = o;
  if (typeof path !== 'string' || !path || path.length > MAX_PATH || /[\u0000-\u001f\u007f]/.test(path) || path.startsWith('/'))
    return null;
  if (!isLine(line)) return null;
  if (start !== undefined && (!isLine(start) || start > line)) return null;
  if (side !== 'LEFT' && side !== 'RIGHT') return null;
  if (typeof commit !== 'string' || !/^[0-9a-f]{40}$/.test(commit)) return null;
  const m: AnchorMarker = { v: 1, path, line, side, commit };
  if (start !== undefined && start < line) m.start = start;
  return m;
}

const HEADER = /^\*\*`+ ?[^\n]*? ?`+ lines? \d+(?:–\d+)?\*\* · \[view\]\([^\s)]*\)$/;

/**
 * Split a comment body that ends with an anchor marker. Null when the body
 * has no marker at its end or the marker is malformed. GitHub returns
 * bodies with CRLF line ends; the parts use LF.
 */
export function parseOffDiff(body: string | null | undefined): OffDiffBody | null {
  if (!body) return null;
  const s = body.replace(/\r\n?/g, '\n');
  const at = s.lastIndexOf(MARKER_OPEN);
  if (at < 0) return null;
  const tail = /^<!-- changelist:anchor (\{[^\n]*\}) -->\s*$/.exec(s.slice(at));
  if (!tail) return null;
  let marker: AnchorMarker | null;
  try {
    marker = normalizeMarker(JSON.parse(tail[1]));
  } catch {
    return null;
  }
  if (!marker) return null;
  const before = s.slice(0, at);
  const lines = before.split('\n');
  let quote: string[] | null = null;
  let headerLen = 0;
  if (HEADER.test(lines[0])) {
    let i = 1;
    const q: string[] = [];
    while (i < lines.length && /^>( |$)/.test(lines[i])) q.push(unescapeMarkdown(lines[i++].replace(/^> ?/, '')));
    quote = q;
    if (i < lines.length && lines[i] === '') i++;
    headerLen = lines.slice(0, i).join('\n').length + (i < lines.length ? 1 : 0);
  }
  const rest = before.slice(headerLen);
  const body_ = rest.replace(/\s+$/, '');
  const lead = body_.length - body_.replace(/^\n+/, '').length;
  return {
    marker,
    text: body_.slice(lead),
    quote,
    prefix: s.slice(0, headerLen + lead),
    suffix: rest.slice(body_.length) + s.slice(at),
  };
}

/** The body with the user's text replaced (header and marker kept). */
export function replaceOffDiffText(body: string, text: string): string {
  const p = parseOffDiff(body);
  if (!p) return text;
  return p.prefix + text.replace(/\r\n?/g, '\n').trim() + p.suffix;
}

// ---------------------------------------------------------------- placement

/** One side of the file as a view shows it. */
export interface ViewSide {
  /** Commit whose text this side shows, when known. */
  commit: string | null;
  lines: string[];
}

/** True when `current` (a source line) is the line a header quoted. */
export function matchesQuote(current: string, quoted: string): boolean {
  return quoteText(current) === quoted;
}

/**
 * Where an anchored comment goes in a view: at its line on the side whose
 * commit is the anchor's, else on its own side when that line's text is
 * still the quoted line; null when neither holds (the comment is on an
 * earlier version and goes to the top of the file).
 */
export function placeAnchor(
  a: Pick<OffDiffBody, 'marker' | 'quote'>,
  view: { LEFT?: ViewSide | null; RIGHT?: ViewSide | null },
): { side: DiffSide; line: number } | null {
  const m = a.marker;
  const own = view[m.side];
  const other = view[m.side === 'LEFT' ? 'RIGHT' : 'LEFT'];
  if (own?.commit === m.commit && m.line <= own.lines.length) return { side: m.side, line: m.line };
  if (other?.commit === m.commit && m.line <= other.lines.length) return { side: m.side === 'LEFT' ? 'RIGHT' : 'LEFT', line: m.line };
  const quoted = a.quote?.length ? a.quote[a.quote.length - 1] : null;
  if (own && quoted !== null && m.line <= own.lines.length && matchesQuote(own.lines[m.line - 1], quoted))
    return { side: m.side, line: m.line };
  return null;
}

// ---------------------------------------------------------------- routing

/**
 * Where a new comment goes:
 * - `line`: a line review comment in the pending review (the lines are inside the PR's diff hunks);
 * - `file`: a file-level review thread in the pending review (the file is in the PR, the lines are not in its hunks);
 * - `held`: a held draft on the changelist server, posted as a conversation comment on publish (the PR does not change the file).
 */
export type CommentRoute = 'line' | 'file' | 'held';

export function routeComment(o: { inPr: boolean; commentable: boolean }): CommentRoute {
  if (!o.inPr) return 'held';
  return o.commentable ? 'line' : 'file';
}
